/**
 * CalendarView — Content calendar with weekly/monthly view (T-064).
 *
 * Shows drafted, scheduled, and published posts color-coded by platform.
 * Fetches from GET /api/v1/calendar and GET /api/v1/schedule.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, Filter } from 'lucide-react'
import { Card, Badge, PageHeader, EmptyState } from '../components/Card'
import { layout } from '../utils/layout'

// ── API types ────────────────────────────────────────────────────────────────

interface CalendarEvent {
  id: string
  type: 'scheduled' | 'draft'
  platform?: string
  platforms?: string[]
  status?: string
  date?: string
  content_preview: string
}

const fetchCalendar = (range: string) =>
  fetch(`/api/v1/calendar?range=${range}`).then(r => r.json()) as Promise<{ events: CalendarEvent[]; range: string }>

// ── Platform colour map ───────────────────────────────────────────────────────

const PLATFORM_COLORS: Record<string, string> = {
  instagram: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  twitter: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  x: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  linkedin: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  tiktok: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  youtube: 'bg-red-500/20 text-red-300 border-red-500/30',
  facebook: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
}

const STATUS_BADGE: Record<string, 'slate' | 'yellow' | 'green' | 'blue'> = {
  draft: 'slate',
  reviewed: 'yellow',
  approved: 'green',
  scheduled: 'blue',
  published: 'green',
}

function platformColor(platform?: string) {
  return platform ? (PLATFORM_COLORS[platform.toLowerCase()] ?? 'bg-slate-700 text-slate-300 border-slate-600') : 'bg-slate-700 text-slate-300 border-slate-600'
}

// ── Day grid helpers ──────────────────────────────────────────────────────────

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay()
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// ── Component ─────────────────────────────────────────────────────────────────

export default function CalendarView() {
  const today = new Date()
  const [view, setView] = useState<'month' | 'week'>('month')
  const [currentDate, setCurrentDate] = useState(today)
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)

  const range = view === 'month' ? 'month' : 'week'

  const { data, isLoading } = useQuery({
    queryKey: ['calendar', range],
    queryFn: () => fetchCalendar(range),
  })

  const events = data?.events ?? []

  // Filter by platform
  const filtered = selectedPlatform
    ? events.filter(e =>
        e.platform === selectedPlatform || e.platforms?.includes(selectedPlatform),
      )
    : events

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)

  // Group events by date (YYYY-MM-DD)
  const eventsByDate: Record<string, CalendarEvent[]> = {}
  for (const ev of filtered) {
    const date = ev.date?.slice(0, 10) ?? ''
    if (date) {
      if (!eventsByDate[date]) eventsByDate[date] = []
      eventsByDate[date].push(ev)
    }
  }

  const prevMonth = () =>
    setCurrentDate(new Date(year, month - 1, 1))

  const nextMonth = () =>
    setCurrentDate(new Date(year, month + 1, 1))

  const platforms = ['instagram', 'twitter', 'linkedin', 'tiktok', 'youtube', 'facebook']

  return (
    <div>
      <PageHeader
        title="Content Calendar"
        subtitle="Schedule · Draft · Publish — all in one view"
        action={
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex bg-slate-800 rounded-lg p-0.5 text-xs">
              {(['month', 'week'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1 rounded-md capitalize transition-colors ${
                    view === v
                      ? 'bg-brand-700 text-brand-100'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Controls row */}
        <div className="flex items-center justify-between">
          {/* Month navigator */}
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-slate-200 w-36 text-center">
              {MONTHS[month]} {year}
            </span>
            <button
              onClick={nextMonth}
              className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Platform filter */}
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <div className="flex gap-1">
              <button
                onClick={() => setSelectedPlatform(null)}
                className={`text-xs px-2 py-0.5 rounded-full capitalize transition-colors ${
                  !selectedPlatform
                    ? 'bg-brand-700 text-brand-100'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                All
              </button>
              {platforms.map(p => (
                <button
                  key={p}
                  onClick={() => setSelectedPlatform(p === selectedPlatform ? null : p)}
                  className={`text-xs px-2 py-0.5 rounded-full capitalize transition-colors ${
                    selectedPlatform === p
                      ? 'bg-brand-700 text-brand-100'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total Items', value: events.length },
            { label: 'Scheduled', value: events.filter(e => e.type === 'scheduled').length },
            { label: 'Drafts', value: events.filter(e => e.type === 'draft').length },
            {
              label: 'Approved',
              value: events.filter(e => e.status === 'approved').length,
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="bg-surface-card border border-surface-border rounded-xl p-3 text-center"
            >
              <div className="text-2xl font-bold text-slate-100">{value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-4">
          {/* Calendar grid */}
          <div className="flex-1">
            <Card title="">
              {/* Day labels */}
              <div className="grid grid-cols-7 gap-px mb-1">
                {DAY_LABELS.map(d => (
                  <div key={d} className="text-xs text-slate-500 text-center py-1">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              {isLoading ? (
                <div className="py-16 text-center text-slate-500 text-sm">Loading…</div>
              ) : (
                <div className="grid grid-cols-7 gap-px">
                  {/* Empty cells for first day offset */}
                  {Array.from({ length: firstDay }).map((_, i) => (
                    <div key={`empty-${i}`} className="min-h-[80px]" />
                  ))}

                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                    const dayEvents = eventsByDate[dateStr] ?? []
                    const isToday =
                      day === today.getDate() &&
                      month === today.getMonth() &&
                      year === today.getFullYear()

                    return (
                      <div
                        key={day}
                        className={`min-h-[80px] p-1 rounded border transition-colors ${
                          isToday
                            ? 'border-brand-500/40 bg-brand-900/20'
                            : 'border-surface-border hover:border-slate-600'
                        }`}
                      >
                        <div
                          className={`text-xs font-medium mb-1 w-5 h-5 flex items-center justify-center rounded-full ${
                            isToday ? 'bg-brand-600 text-white' : 'text-slate-400'
                          }`}
                        >
                          {day}
                        </div>

                        {/* Events for this day */}
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 3).map(ev => (
                            <button
                              key={ev.id}
                              onClick={() => setSelectedEvent(ev)}
                              className={`w-full text-left text-[10px] px-1 py-0.5 rounded border truncate transition-opacity hover:opacity-80 ${platformColor(ev.platform ?? ev.platforms?.[0])}`}
                            >
                              {ev.content_preview.slice(0, 30)}
                            </button>
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="text-[10px] text-slate-500 pl-1">
                              +{dayEvents.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {events.length === 0 && !isLoading && (
                <EmptyState
                  icon={<CalendarDays className="w-12 h-12" />}
                  title="No content scheduled"
                  description="Create drafts in the Content Studio and schedule them here."
                />
              )}
            </Card>
          </div>

          {/* Side panel — event detail */}
          {selectedEvent && (
            <div className="w-72 shrink-0">
              <Card title="Post Detail">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded border ${platformColor(selectedEvent.platform ?? selectedEvent.platforms?.[0])}`}
                    >
                      {selectedEvent.platform ?? selectedEvent.platforms?.join(', ') ?? 'Multi-platform'}
                    </span>
                    {selectedEvent.status && (
                      <Badge color={STATUS_BADGE[selectedEvent.status] ?? 'slate'}>
                        {selectedEvent.status}
                      </Badge>
                    )}
                  </div>

                  {selectedEvent.date && (
                    <div className="text-xs text-slate-400">
                      Scheduled: <span className="text-slate-200">{selectedEvent.date.slice(0, 10)}</span>
                    </div>
                  )}

                  <div className="text-sm text-slate-300 bg-slate-800 rounded-lg p-2.5 leading-relaxed">
                    {selectedEvent.content_preview}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedEvent(null)}
                      className="flex-1 text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Card>

              {/* Legend */}
              <Card title="Platform Legend">
                <div className="space-y-1.5">
                  {platforms.map(p => (
                    <div key={p} className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded border ${platformColor(p)}`}>
                        {p}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* Content mix visualization */}
        <Card title="Content Mix">
          <div className="flex items-center gap-4 text-xs text-slate-400">
            {[
              { label: 'Drafts', count: events.filter(e => e.type === 'draft').length, color: 'bg-slate-500' },
              { label: 'Scheduled', count: events.filter(e => e.type === 'scheduled').length, color: 'bg-brand-500' },
            ].map(({ label, count, color }) => {
              const pct = events.length > 0 ? Math.round((count / events.length) * 100) : 0
              return (
                <div key={label} className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${color}`} />
                  <span>{label}: {count} ({pct}%)</span>
                </div>
              )
            })}
            {events.length === 0 && <span className="text-slate-600">No content yet</span>}
          </div>
        </Card>
      </div>
    </div>
  )
}
